import { createFeatureSelector, createSelector } from '@ngrx/store';

import { isClientId } from './chat.models';
import { AI_CHAT_FEATURE_KEY, entityAdapter, type State } from './chat.reducer';
import { pendingAnswer } from './pending-answer';
import {
  newestPersistedMessage,
  pendingTemplateConfigurationSetupStep,
} from './template-configuration-setup-lifecycle';

export const selectChatState = createFeatureSelector<State>(AI_CHAT_FEATURE_KEY);

export const { selectAll, selectEntities } = entityAdapter.getSelectors();

export const selectCurrentConversationState = createSelector(
  selectChatState,
  (state) => state.conversation,
);

export const selectCurrentConversationStatus = createSelector(
  selectCurrentConversationState,
  (state) => state.status,
);

export const selectCurrentConversation = createSelector(
  selectCurrentConversationState,
  (state) => state.conversation,
);

export const selectCurrentConversationError = createSelector(
  selectCurrentConversationState,
  (state) => state.error,
);

export const selectEstablishedConversationProjectId = createSelector(
  selectCurrentConversationState,
  (state) => (state.status === 'success' ? state.conversation?.projectId : undefined),
);

export const selectPanelProjectId = createSelector(
  selectChatState,
  (state) => state.panelProjectId,
);

export const selectCoveredProjectId = createSelector(
  selectChatState,
  ({ conversation, panelProjectId }) =>
    conversation.status === 'failure' ? undefined : panelProjectId,
);

export const selectConversationReadyToSend = createSelector(selectChatState, (state) =>
  state.conversation.status === 'success' &&
  !state.conversation.isRunning &&
  !hasMessageInDelivery(state) &&
  state.hasLoadedMessages
    ? state.conversation.conversation
    : undefined,
);

function hasMessageInDelivery(state: State): boolean {
  return (state.ids as string[]).some(isClientId);
}

export const selectHasLoadedMessages = createSelector(
  selectChatState,
  (state) => state.hasLoadedMessages,
);

export const selectRunningTemplateConfigurationSetup = createSelector(
  selectChatState,
  (state) => state.runningTemplateConfigurationSetup,
);

export const selectIsTemplateConfigurationSetupRunning = createSelector(
  selectRunningTemplateConfigurationSetup,
  Boolean,
);

export const selectPendingTemplateConfigurationSetupStep = createSelector(
  selectChatState,
  selectIsTemplateConfigurationSetupRunning,
  (state, isRunning) => (isRunning ? pendingTemplateConfigurationSetupStep(state) : undefined),
);

export const selectPendingAnswer = createSelector(selectChatState, pendingAnswer);

export const selectIsConversationRunning = createSelector(
  selectCurrentConversationState,
  (state) => state.isRunning,
);

export const selectContextUsage = createSelector(
  selectCurrentConversationState,
  (state) => state.contextUsage,
);

export const selectPollingState = createSelector(selectChatState, (state) => state.polling);

export const selectPollingStatus = createSelector(selectPollingState, (state) => state.status);

export const selectPollingError = createSelector(selectPollingState, (state) => state.error);

export const selectLivePollingConversationId = createSelector(selectPollingState, (state) =>
  state.status === 'pending' ? state.chatConversationId : undefined,
);

export const selectSendError = createSelector(selectChatState, (state) => state.sendError);

export const selectLastFailedMessage = createSelector(
  selectChatState,
  (state) => state.lastFailedMessage,
);

export const selectFeedback = createSelector(selectChatState, (state) => state.feedback);

export const selectPendingActionTarget = createSelector(
  selectChatState,
  (state) => state.pendingActionTarget,
);

export const selectInterruptError = createSelector(
  selectChatState,
  (state) => state.interruptError,
);

export const selectNewConversationBoundaryMessageIds = createSelector(
  selectChatState,
  (state) => state.newConversationBoundaryMessageIds,
);

export const selectAllMessages = createSelector(selectChatState, selectAll);

export const selectLastPersistedMessageId = createSelector(
  selectChatState,
  (state) => newestPersistedMessage(state)?.id,
);

export const selectIsUserWaitingForAnswer = createSelector(
  selectIsConversationRunning,
  selectAllMessages,
  (conversationRunning, messages) => {
    const lastMessage = messages.at(-1);
    return conversationRunning || (!!lastMessage && isClientId(lastMessage.id));
  },
);
